'use client'

import { RiArrowLeftSLine, RiArrowRightSLine, RiCalendarLine } from '@remixicon/react'
import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '../Button'

// 'month-year' always applies a month+year. 'month-or-year' uses the active tab
// to decide: the "Select Year" tab applies a whole year (month undefined), the
// "Select Month" tab applies that month.
type DatePickerMode = 'month-year' | 'year' | 'month' | 'month-or-year'

const TABBED_MODES: DatePickerMode[] = ['month-year', 'month-or-year']

interface DatePickerModalProps {
    mode?: DatePickerMode
    selectedMonth?: number
    selectedYear?: number
    onApply: (month?: number, year?: number) => void
    onCancel?: () => void
    dateLabel?: string
    triggerClassName?: string
    triggerLabelClassName?: string
}

const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr',
    'May', 'Jun', 'Jul', 'Aug',
    'Sep', 'Oct', 'Nov', 'Dec'
]

const YEAR_RANGE = 12 // Show 12 years per page

export const DatePickerModal: React.FC<DatePickerModalProps> = ({
    mode = 'month-year',
    selectedMonth: initialMonth,
    selectedYear: initialYear,
    onApply,
    onCancel,
    dateLabel,
    triggerClassName,
    triggerLabelClassName
}) => {
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth()

    // Open on the tab that matches the current selection: in 'month-or-year'
    // a caller with no month is showing a whole year, so starting on the month
    // tab would misrepresent the period it is bound to. Every other mode keeps
    // the original 'month' default.
    const defaultTab = (): 'month' | 'year' =>
        mode === 'month-or-year' && initialMonth === undefined ? 'year' : 'month'

    const [isOpen, setIsOpen] = useState(false)
    const [activeTab, setActiveTab] = useState<'month' | 'year'>(defaultTab)
    const [selectedMonth, setSelectedMonth] = useState<number | undefined>(initialMonth ?? currentMonth)
    const [selectedYear, setSelectedYear] = useState<number>(initialYear ?? currentYear)
    const [yearRangeStart, setYearRangeStart] = useState<number>(
        Math.floor((initialYear ?? currentYear) / YEAR_RANGE) * YEAR_RANGE
    )

    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        if (initialMonth !== undefined) {
            setSelectedMonth(initialMonth)
        }
    }, [initialMonth])

    useEffect(() => {
        if (initialYear !== undefined) {
            setSelectedYear(initialYear)
            setYearRangeStart(Math.floor(initialYear / YEAR_RANGE) * YEAR_RANGE)
        }
    }, [initialYear])

    const handleOpen = () => {
        setActiveTab(defaultTab())
        setIsOpen(true)
    }
    const handleClose = () => {
        setIsOpen(false)
        setActiveTab(defaultTab())
    }

    const handlePrevYear = () => {
        setSelectedYear(prev => prev - 1)
    }

    const handleNextYear = () => {
        setSelectedYear(prev => prev + 1)
    }

    const handlePrevYearRange = () => {
        setYearRangeStart(prev => prev - YEAR_RANGE)
    }

    const handleNextYearRange = () => {
        setYearRangeStart(prev => prev + YEAR_RANGE)
    }

    const handleMonthSelect = (monthIndex: number) => {
        setSelectedMonth(monthIndex)
        if (mode === 'month') {
            // Auto-apply for month-only mode if needed
        }
    }

    const handleYearSelect = (year: number) => {
        setSelectedYear(year)
        if (mode === 'year') {
            // Auto-apply for year-only mode if needed
        }
    }

    const handleApply = () => {
        if (mode === 'month-or-year') {
            // Active tab decides month-specific vs whole-year.
            onApply(activeTab === 'year' ? undefined : selectedMonth, selectedYear)
        } else if (mode === 'month-year') {
            onApply(selectedMonth, selectedYear)
        } else if (mode === 'year') {
            onApply(undefined, selectedYear)
        } else {
            onApply(selectedMonth, selectedYear)
        }
        handleClose()
    }

    const handleCancel = () => {
        onCancel?.()
        handleClose()
    }

    const getDisplayValue = () => {
        if (dateLabel) return dateLabel

        if (mode === 'year') {
            return `Year ${selectedYear}`
        } else if (mode === 'month' && selectedMonth !== undefined) {
            return `${MONTHS[selectedMonth]}, ${selectedYear}`
        } else if (selectedMonth !== undefined) {
            return `${MONTHS[selectedMonth]}, ${selectedYear}`
        }
        return 'Select date'
    }

    const renderYearGrid = () => {
        const years = Array.from({ length: YEAR_RANGE }, (_, i) => yearRangeStart + i)
        const rows: React.ReactElement[] = []

        for (let i = 0; i < years.length; i += 4) {
            const rowYears = years.slice(i, i + 4)
            rows.push(
                <div key={i} className="self-stretch inline-flex justify-start items-center gap-2.5">
                    {rowYears.map(year => (
                        <button
                            key={year}
                            onClick={() => handleYearSelect(year)}
                            className={`flex-1 px-2 py-[10px] rounded-md flex justify-center items-center gap-2.5 hover:bg-slate-100 transition-colors cursor-pointer ${year === selectedYear ? 'bg-sky-700' : ''
                                }`}
                        >
                            <div className={`text-center text-sm font-medium font-inter leading-5 ${year === selectedYear ? 'text-white' : 'text-[#34405499]'
                                }`}>
                                {year}
                            </div>
                        </button>
                    ))}
                </div>
            )
        }

        return rows
    }

    const renderMonthGrid = () => {
        const rows: React.ReactElement[] = []

        for (let i = 0; i < MONTHS.length; i += 4) {
            const rowMonths = MONTHS.slice(i, i + 4)
            rows.push(
                <div key={i} className="self-stretch inline-flex justify-start items-center gap-2.5">
                    {rowMonths.map((month, idx) => {
                        const monthIndex = i + idx
                        return (
                            <button
                                key={month}
                                onClick={() => handleMonthSelect(monthIndex)}
                                className={`flex-1 px-2 py-[10px] rounded-md flex justify-center items-center gap-2.5 hover:bg-slate-100 transition-colors cursor-pointer ${monthIndex === selectedMonth ? 'bg-sky-700' : ''
                                    }`}
                            >
                                <div className={`text-center text-sm font-medium font-poppins leading-5 ${monthIndex === selectedMonth ? 'text-white font-inter' : 'text-[#34405499]'
                                    }`}>
                                    {month}
                                </div>
                            </button>
                        )
                    })}
                </div>
            )
        }

        return rows
    }

    const renderHeader = () => {
        if (TABBED_MODES.includes(mode)) {
            return (
                <div className="self-stretch px-6 py-5 border-b border-zinc-200 inline-flex justify-start items-start gap-5">
                    <button
                        onClick={() => setActiveTab('month')}
                        className={`flex-1 px-4 py-2.5 rounded-md flex justify-center items-center overflow-hidden transition-colors cursor-pointer ${activeTab === 'month'
                            ? 'bg-[#D9E7EF]'
                            : 'outline-transparent hover:bg-slate-50'
                            }`}
                    >
                        <div className="text-slate-700 text-sm font-medium font-poppins leading-5">
                            Select Month
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('year')}
                        className={`flex-1 px-4 py-2.5 rounded-md flex justify-center items-center overflow-hidden transition-colors cursor-pointer ${activeTab === 'year'
                            ? 'bg-[#D9E7EF]'
                            : 'outline-transparent hover:bg-slate-50'
                            }`}
                    >
                        <div className="text-slate-700 text-sm font-medium font-poppins leading-5">
                            Select Year
                        </div>
                    </button>
                </div>
            )
        } else {
            return (
                <div className="self-stretch px-6 py-5 border-b border-zinc-200 inline-flex justify-start items-start gap-5">
                    <div className="text-slate-700 text-sm font-medium font-poppins leading-5">
                        {mode === 'year' ? 'Select year' : 'Select Month'}
                    </div>
                </div>
            )
        }
    }

    const renderContent = () => {
        const showYearView = mode === 'year' || (TABBED_MODES.includes(mode) && activeTab === 'year')
        const showMonthView = mode === 'month' || (TABBED_MODES.includes(mode) && activeTab === 'month')

        if (showYearView) {
            return (
                <>
                    <div className="self-stretch inline-flex justify-between items-center">
                        <button
                            onClick={handlePrevYearRange}
                            className="p-2 rounded-lg flex justify-center items-center gap-2 overflow-hidden hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                            <RiArrowLeftSLine className="w-5 h-5 text-gray-500" />
                        </button>
                        <div className="text-center text-neutral-800 text-base font-semibold font-poppins leading-6">
                            {yearRangeStart}-{yearRangeStart + YEAR_RANGE - 1}
                        </div>
                        <button
                            onClick={handleNextYearRange}
                            className="p-2 rounded-lg flex justify-center items-center gap-2 overflow-hidden hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                            <RiArrowRightSLine className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                    {renderYearGrid()}
                </>
            )
        }

        if (showMonthView) {
            return (
                <>
                    <div className="self-stretch inline-flex justify-between items-center">
                        <button
                            onClick={handlePrevYear}
                            className="p-2 rounded-lg flex justify-center items-center gap-2 overflow-hidden hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                            <RiArrowLeftSLine className="w-5 h-5 text-gray-500" />
                        </button>
                        <div className="text-center text-neutral-800 text-base font-semibold font-poppins leading-6">
                            {selectedYear}
                        </div>
                        <button
                            onClick={handleNextYear}
                            className="p-2 rounded-lg flex justify-center items-center gap-2 overflow-hidden hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                            <RiArrowRightSLine className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                    {renderMonthGrid()}
                </>
            )
        }

        return null
    }

    const renderModal = () => {
        if (!isOpen || !mounted) return null

        return createPortal(
            <div
                className="fixed inset-0 flex items-center justify-center transition-opacity duration-200"
                onClick={handleClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    zIndex: 2147483647
                }}
            >
                <div
                    className="bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col transition-all duration-200 scale-100 opacity-100"
                    onClick={(e) => e.stopPropagation()}
                    style={{ position: 'relative', zIndex: 2147483647, width: '480px', maxWidth: '90vw',
                        // Short phone viewports (and landscape) would clip the
                        // Apply row off-screen without this.
                        maxHeight: '90vh', overflowY: 'auto' }}
                >
                    {renderHeader()}

                    <div className="px-6 py-4 flex flex-col gap-4">
                        {renderContent()}
                    </div>

                    <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                        <div className="px-3.5 py-2 bg-slate-50 rounded-lg border border-zinc-200 flex items-center gap-2">
                            <RiCalendarLine className="w-4 h-4 text-neutral-800" />
                            <div className="text-gray-900 text-sm font-medium font-poppins truncate">
                                {getDisplayValue()}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                buttonType="button"
                                variant="default"
                                onClick={handleCancel}
                                styles={{ label: { color: '#334155' } }}
                                className="!px-4 !py-2 !bg-white !rounded-lg !border !border-zinc-200 !text-slate-700 !text-sm !font-semibold !font-inter hover:!bg-slate-50 !transition-colors !cursor-pointer"
                            >
                                Cancel
                            </Button>
                            <Button
                                buttonType="button"
                                onClick={handleApply}
                                className="!px-4 !py-2 !bg-sky-700 !rounded-lg !shadow-sm !text-white !text-sm !font-semibold !font-inter hover:!bg-sky-800 !transition-colors !cursor-pointer"
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        )
    }

    return (
        <>
            <button
                onClick={handleOpen}
                className={[
                    'flex h-[30px] px-3 py-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer',
                    triggerClassName
                ].filter(Boolean).join(' ')}
            >
                <RiCalendarLine size={15} className="text-gray-900" />
                <span className={[
                    'text-xs font-medium text-gray-900 font-poppins leading-[160%] truncate',
                    triggerLabelClassName
                ].filter(Boolean).join(' ')}>
                    {getDisplayValue()}
                </span>
            </button>

            {renderModal()}
        </>
    )
}
